"use client"

import { useQuery } from "@tanstack/react-query"
import { queryKeys } from "@/shared/lib/query-keys"
import { getCoinBalance } from "@/features/credits/actions"
import { useAuthStore } from "@/features/auth/store"
import type { CoinBalanceRow } from "@/shared/lib/supabase/types"

const DEFAULT_BALANCE: CoinBalanceRow = {
  user_id: "",
  balance: 0,
  lifetime_earned: 0,
  lifetime_spent: 0,
  last_grant_at: null,
  updated_at: new Date().toISOString(),
}

/**
 * React Query hook for the current user's coin balance.
 * Polls every 30s to keep the topbar badge fresh.
 * Uses placeholderData so the displayed value doesn't flash when refetching or when query key updates.
 */
export function useCoinBalance() {
  const user = useAuthStore((s) => s.user)
  const userId = user?.id ?? ""

  const query = useQuery({
    queryKey: queryKeys.coins.balance(userId),
    queryFn: async () => {
      const result = await getCoinBalance()
      if (!result.success || !result.balance) {
        throw new Error(result.error ?? "Failed to fetch coin balance")
      }
      return result.balance
    },
    enabled: !!userId,
    staleTime: 30_000,       // 30s — balance doesn't change that often
    refetchInterval: 60_000, // Refetch every 60s in background
    placeholderData: (previousData) =>
      previousData && previousData.user_id === userId ? previousData : undefined,
  })

  return {
    balance: query.data ?? DEFAULT_BALANCE,
    coins: query.data?.balance ?? 0,
    isLoading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: query.refetch,
  }
}
